<template>
  <button
    ref="buttonRef"
    :class="[buttonsVariants({ typeButton, size }), $options.name]"
    :data-e-ga-id="uniqueId"
    :data-testid="dataTestId || uniqueId"
    :disabled="disabled"
    :form="form"
    :type="nativeType"
    @click="handleClick"
  >
    <EdroneButtonBorderAnimation v-if="hasBorderAnimation" :type-button="typeButton" />
    <transition
      enter-active-class="tw:transition tw:duration-200"
      enter-from-class="tw:opacity-0"
      leave-active-class="tw:transition tw:duration-200"
      leave-to-class="tw:opacity-0"
      mode="out-in"
    >
      <div v-if="loading">
        <font-awesome-icon class="" icon="fa-regular fa-spinner-third" spin />
      </div>
      <!--  @slot Use this slot for icon that appears on the left side of the text -->
      <div v-else-if="$slots['left-icon']" class="tw:flex tw:items-center tw:justify-center">
        <slot name="left-icon" />
      </div>
    </transition>
    <slot />
    <!--  @slot Use this slot for icon that appears on the right side of the text -->
    <slot v-if="$slots['right-icon']" name="right-icon" />
    <!--  @slot Use this slot for badge that appears on the right side of the button -->
    <slot v-if="$slots['badge'] && size !== 'small'" name="badge" />
  </button>
</template>

<script lang="ts">
import { cva } from "class-variance-authority";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { computed, defineComponent, ref } from "vue";
import type { PropType } from "vue";
import type { ButtonType } from "./EdroneButton.types";
import EdroneButtonBorderAnimation from "edrone-components/EdroneButtonBorderAnimation.vue";

export default defineComponent({
  name: "EdroneButton",
  components: {
    EdroneButtonBorderAnimation,
  },
  props: {
    form: {
      type: String,
      required: false,
    },
    nativeType: {
      type: String as PropType<"submit" | "button" | "reset">,
      default: "button",
    },
    type: {
      type: String as PropType<ButtonType>,
      default: "solid",
    },
    /**
     * Whether the button should be rendered with a loading icon.
     * The button won't be clickable while loading.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the button should be rendered as disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<"big" | "medium" | "small">,
      default: "big",
    },
    uniqueId: {
      type: String,
      required: false,
      default: "",
    },
    dataTestId: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * DEPRECATED: Use `type` instead
     * Whether the button should be rendered with styles as a link
     */
    link: {
      type: Boolean,
      default: false,
      validator(value: boolean) {
        return !value;
      },
    },
    /**
     * Backwards compatibility with the old `EdroneButton` component
     * DEPRECATED: Use `type` instead
     */
    primary: {
      type: Boolean,
      default: false,
      validator(value: boolean) {
        return !value;
      },
    },
    /**
     * Backwards compatibility with the old `EdroneButton` component
     * DEPRECATED: Use `type` instead
     */
    text: {
      type: Boolean,
      default: false,
      validator(value: boolean) {
        return !value;
      },
    },
    /**
     * Backwards compatibility with the old `EdroneButton` component
     * DEPRECATED and REMOVED: Use `type` instead
     */
    dark: {
      type: Boolean,
      default: false,
      validator(value: boolean) {
        return !value;
      },
    },
  },
  emits: {
    click: () => true,
    clickSelf: (event: MouseEvent) => event,
  },
  setup(props, { emit }) {
    const buttonRef = ref<HTMLButtonElement | null>(null);

    const buttonsVariants = cva(
      "tw:font-roboto tw:flex tw:items-center tw:justify-center tw:border tw:text-center tw:relative tw:rounded-sm tw:whitespace-nowrap tw:transition-all tw:duration-300 tw:body-1",
      {
        variants: {
          typeButton: {
            solid:
              "tw:text-black tw:bg-branding-500 tw:border-black tw:outline tw:outline-1 tw:outline-transparent tw:hover:outline-black tw:disabled:bg-branding-000 tw:disabled:border-default-200 tw:disabled:text-default-500 tw:disabled:outline-hidden",
            outline:
              "tw:text-black tw:bg-white tw:border-default-200 tw:hover:bg-default-000 tw:hover:border-default-400 tw:disabled:bg-white tw:disabled:text-default-500",
            text: "tw:bg-transparent tw:text-black tw:border-none tw:hover:bg-default-100 tw:disabled:border-transparent tw:disabled:bg-white tw:disabled:text-default-500",
            danger: "tw:bg-red-500 tw:border-transparent tw:text-white tw:hover:bg-red-600 tw:disabled:bg-red-000",
            "danger-outline":
              "tw:bg-white tw:border-red-500 tw:text-red-500 tw:hover:bg-red-000 tw:hover:text-red-600 tw:hover:border-red-500 tw:disabled:bg-white tw:disabled:border-red-200 tw:disabled:text-red-200",
            "danger-text":
              "tw:border-transparent tw:text-red-500 tw:hover:bg-red-000 tw:disabled:bg-white tw:disabled:text-red-200 ",
            link: "tw:border-transparent tw:bg-blue-500 tw:text-white tw:hover:bg-blue-600 tw:disabled:bg-blue-000",
            "link-outline":
              "tw:bg-white tw:border-blue-200 tw:text-blue-500 tw:hover:bg-blue-000 tw:hover:border-blue-400 tw:disabled:bg-white tw:disabled:border-blue-200 tw:disabled:text-blue-200",
            "link-text":
              "tw:bg-transparent tw:border-transparent tw:text-blue-500 tw:hover:bg-blue-000 tw:hover:text-blue-600 tw:disabled:bg-white tw:disabled:text-blue-200",
            "ai-solid": `
              tw:relative tw:text-violet-600 tw:bg-violet-100 tw:shadow-violet-glow tw:border-violet-200 
              tw:hover:shadow-[0_0_8px_3px_rgba(151,71,255,0.24),0_0_0_1px_theme(colors.violet.400)] tw:hover:border-violet-400 
              tw:disabled:bg-violet-000 tw:disabled:text-violet-200 tw:disabled:border-violet-200 tw:disabled:shadow-none
            `,
            "ai-outline": `
              tw:relative tw:text-violet-600 tw:bg-white tw:shadow-violet-glow tw:border-violet-100 
              tw:hover:shadow-[0_0_8px_3px_rgba(151,71,255,0.24),0_0_0_1px_theme(colors.violet.200)] tw:hover:border-violet-200 
              tw:disabled:bg-white tw:disabled:text-violet-200 tw:disabled:border-violet-100 tw:disabled:shadow-none
            `,
            "ai-text": `
              tw:relative tw:text-violet-600 tw:bg-white tw:border-none 
              tw:hover:bg-violet-000 
              tw:disabled:bg-white tw:disabled:text-violet-200
            `,
          },
          size: {
            big: "tw:gap-3 tw:px-4 tw:py-2 tw:max-h-10 tw:h-[40px]",
            medium: "tw:gap-2 tw:px-[10px] tw:py-[6px] tw:text-sm tw:h-[34px]",
            small: "tw:gap-2 tw:px-2 tw:py-1 tw:text-xs tw:h-[26px]",
          },
        },
      }
    );

    const typeButton = computed(() => {
      if (props.primary) {
        return "solid";
      }

      if (props.text) {
        return "text";
      }

      if (props.link) {
        return "link";
      }

      if (props.dark) {
        return "solid";
      }

      if (props.type) {
        return props.type;
      }

      return "outline";
    });

    const hasBorderAnimation = computed(
      () => (props.type === "ai-solid" || props.type === "ai-outline") && !props.disabled
    );

    function handleClick(event: MouseEvent | KeyboardEvent) {
      emit("click");
      emit("clickSelf", event as MouseEvent);
      gtagUniversalSend({ component_id: props.uniqueId });
    }

    return {
      typeButton,
      buttonRef,
      handleClick,
      buttonsVariants,
      hasBorderAnimation,
    };
  },
});
</script>
